import './eiq-connect-fast-access-pass.scss';
import React, { useState, useEffect } from 'react';
import { Col, Divider, Row, QRCode } from 'antd';
import LogoIcon from 'assets/icons/eiq-connect/eiq-connect-logo-with-trademark.png';
import communityResolverService from 'common/services/community-resolver-service/community-resolver.service';
import fastAccessService from 'common/services/fast-access-service/fast-access-service';
import { useParams } from 'react-router-dom';
import { IFastAccessPassDetails } from 'common/services/fast-access-service/fast-access-service.types';
import { formatAddressLabel } from 'common/helpers/address.helper';
import { formatEiqPassportCode } from 'common/helpers/eiq-passport.helper';
import AddToWalletBtn from './add-to-wallet-btn/add-to-wallet-btn';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import { findIana } from 'windows-iana';
import CountdownTimer from 'common/components/сountdown-timer/сountdown-timer';

dayjs.extend(timezone);

const FastAccessPass = () => {
  const [fastAccessPass, setFastAccessPass] = useState<IFastAccessPassDetails | null>(null);
  const { communityId, fastAccessPassId } = useParams();

  const isIPhoneOrAndroid = () => {
    const userAgent = navigator.userAgent;
    return /IPhone/i.test(userAgent);
  };

  useEffect(() => {
    if (!communityId || !fastAccessPassId) {
      return;
    }

    communityResolverService.resolveByCommunityId(communityId).then((community) => {
      fastAccessService.getFastAccessPassDetails(fastAccessPassId, community.communityApiUri).then((fastAccessPass) => {
        setFastAccessPass(fastAccessPass);
      });
    });
  }, []);

  const handleAddToWallet = async () => {
    if (!communityId || !fastAccessPassId) {
      return;
    }
    var community = await communityResolverService.resolveByCommunityId(communityId);
    var file = await fastAccessService.getFastAccessForPassAppleWallet(fastAccessPassId, community.communityApiUri);

    const downloadUrl = window.URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = 'Add-to-Wallet.pkpass';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getValidRow = (dateFrom: any, dateTo: any, timeZoneId?: string | null) => {
    const cleanDate = (date?: string | null) => {
      if (!date) {
        return date;
      }

      const result = date.replace(/(\.\d{3})\d+/, '$1').replace(/([+-]\d{2}:\d{2}|Z)$/, '');
      return result;
    };

    const getOffSetInMinutes = () => {
      const zone = findIana(timeZoneId ?? 'Eastern Standard Time')?.[0] ?? 'UTC';
      const now = dayjs();
      const zonedNow = now.tz(zone);
      return zonedNow.utcOffset();
    };

    const offSetInMinutes = getOffSetInMinutes();

    const from = cleanDate(dateFrom);
    const to = cleanDate(dateTo);

    const fromDate = from ? dayjs.utc(from).add(offSetInMinutes, 'minute') : null;
    const toDate = to ? dayjs.utc(to).add(offSetInMinutes, 'minute') : null;

    if (fromDate && toDate) {
      if (fromDate.startOf('day').isSame(toDate.startOf('day'))) {
        return fromDate.format('MMM D, YYYY');
      }
    }

    return `${fromDate?.format('MMM D, YYYY')} - ${toDate ? toDate.format('MMM D, YYYY') : 'Permanent'}`;
  };

  return (
    <>
      <div className="fast-access-pass">
        <div className="logo-wrapper">
          <img className="logo" src={fastAccessPass?.communityLogoUrl ?? LogoIcon} alt="logo" />
        </div>
        <Row className={'row'} style={{ paddingBottom: '6px' }}>
          <Col className="col">
            <div className="guest-name">Hello {fastAccessPass?.name},</div>
            <div className="guest-text">{fastAccessPass?.instruction ?? 'Present this QR code and your photo ID at the gate.'}</div>
            <div className="fast-access-wrapper">
              {fastAccessPass?.id && (
                <div className="running-border">
                  <QRCode size={300} value={formatEiqPassportCode(fastAccessPass.id)} bordered={false} />
                </div>
              )}
            </div>
            <CountdownTimer expires={fastAccessPass?.expires!} expiredMessage={'QR code has expired'} />
          </Col>
        </Row>
        <div className="speed-limit-image-container">
          {fastAccessPass?.speedLimitImageUrl && (
            <img className="speed-limit-image" src={fastAccessPass?.speedLimitImageUrl} alt="speed limit image" />
          )}
          {!fastAccessPass?.speedLimitImageUrl && fastAccessPass?.speedLimit && (
            <div className="speed-limit-wrapper">
              <div className="speed">Speed</div>
              <div className="limit">Limit</div>
              <div className="speed-limit">{fastAccessPass.speedLimit}</div>
            </div>
          )}
        </div>
        <div className="community">
          <div className="title">Community</div>
          <div className="text">{fastAccessPass?.community}</div>
        </div>
        <Divider className="auth-diver" />
        <div className="from">
          <div className="title">From</div>
          <div className="text">{fastAccessPass?.residentName}</div>
        </div>
        <Divider className="auth-diver" />
        <div className="title">Address</div>
        <div className="text">
          <a
            target="_blank"
            rel="noreferrer"
            href={`https://maps.google.com/?q=${fastAccessPass?.state}, ${fastAccessPass?.city}, ${formatAddressLabel(
              fastAccessPass?.address,
            )}`}>
            {formatAddressLabel(fastAccessPass?.address)}
          </a>
        </div>
        <Divider className="auth-diver" />
        <div className="title">Valid</div>
        <div className="text">{getValidRow(fastAccessPass?.validFrom, fastAccessPass?.expires, fastAccessPass?.timeZoneId)}</div>
        <Divider className="auth-diver" />
        <div className="title">Community Rules</div>
        <div className="text">{fastAccessPass?.communityRules ? fastAccessPass?.communityRules : 'None'}</div>
        <Divider className="auth-diver" />
        {isIPhoneOrAndroid() ? <AddToWalletBtn onClick={() => handleAddToWallet().then()} /> : null}
      </div>
    </>
  );
};

export default FastAccessPass;
